import { useEffect, useState } from 'react'

import { apiGetCheckPoints, apiGetRequiredMiles } from '@src/api/point.api'
import { ChainId, NATIVE_TOKEN_ADDRESS, isTestnet } from '@src/configs/chainInfo'
import { MIN_MILES_FOR_MON_PURCHASE, MONAD_TESTNET_WMON_ADDRESS } from '@src/constants/miles'
import { IExchangeData } from '@src/types/exchange'
import { createContextLogger, isFeatureEnabled } from '@src/utils'

const logger = createContextLogger('useMilesRequirement')

// Hook to fetch required miles dynamically
const useRequiredMiles = () => {
    const [requiredMiles, setRequiredMiles] = useState<number>(MIN_MILES_FOR_MON_PURCHASE)
    const [isLoadingRequiredMiles, setIsLoadingRequiredMiles] = useState<boolean>(false)

    useEffect(() => {
        const fetchRequiredMiles = async () => {
            setIsLoadingRequiredMiles(true)
            try {
                const response = await apiGetRequiredMiles()
                setRequiredMiles(response.required_miles)
                logger.info('Fetched required miles:', response.required_miles)
            } catch (error) {
                logger.warn('Failed to fetch required miles, using fallback constant:', error)
                setRequiredMiles(MIN_MILES_FOR_MON_PURCHASE)
            } finally {
                setIsLoadingRequiredMiles(false)
            }
        }

        fetchRequiredMiles()
    }, [])

    return { requiredMiles, isLoadingRequiredMiles }
}

interface UseMilesRequirementProps {
    buyData: IExchangeData
    sellData: IExchangeData
    mainWalletAddress: string | undefined
}

// Rename interface
export interface MilesRequirementStatus {
    hasEnoughMiles: boolean
    currentMiles: number
    isLoadingMiles: boolean
    error: string | null
    requiresMiles: boolean
    requiredMiles: number
    isLoadingRequiredMiles: boolean
}

// Rename hook
export const useMilesRequirement = ({ buyData, sellData, mainWalletAddress }: UseMilesRequirementProps) => {
    const { requiredMiles, isLoadingRequiredMiles } = useRequiredMiles()

    const [milesStatus, setMilesStatus] = useState<MilesRequirementStatus>({
        hasEnoughMiles: false,
        currentMiles: 0,
        isLoadingMiles: false,
        error: null,
        requiresMiles: false,
        requiredMiles: MIN_MILES_FOR_MON_PURCHASE,
        isLoadingRequiredMiles: false,
    })

    const isMonadTestnetToken = (data: IExchangeData) => {
        return data.token?.chainId === ChainId.MonadTestnet
    }

    const isMegaEthToken = (data: IExchangeData) => {
        return data.token?.chainId === ChainId.MegaEthTestnet
    }

    const checkRequiresMiles = () => {
        if (
            isFeatureEnabled('MILES_REQUIREMENT') &&
            (isMonadTestnetToken(buyData) ||
                isMonadTestnetToken(sellData) ||
                isMegaEthToken(sellData) ||
                isMegaEthToken(buyData))
        ) {
            return true
        } else {
            return false
        }
    }

    const fetchMiles = async (address: string) => {
        setMilesStatus(prev => ({ ...prev, isLoadingMiles: true, error: null }))
        try {
            const { points: miles } = await apiGetCheckPoints(address)
            setMilesStatus(prev => ({
                ...prev,
                hasEnoughMiles: miles >= requiredMiles,
                currentMiles: miles,
                isLoadingMiles: false,
                error: null,
                requiresMiles: checkRequiresMiles(),
                requiredMiles,
                isLoadingRequiredMiles,
            }))
        } catch (error) {
            logger.error('Failed to fetch miles:', error)
            setMilesStatus(prev => ({
                ...prev,
                isLoadingMiles: false,
                error: 'Failed to fetch miles',
                requiresMiles: checkRequiresMiles(),
                requiredMiles,
                isLoadingRequiredMiles,
            }))
        }
    }

    useEffect(() => {
        const requiresMiles = checkRequiresMiles()

        if (requiresMiles && mainWalletAddress) {
            fetchMiles(mainWalletAddress)
        } else {
            setMilesStatus(prev => ({
                ...prev,
                requiresMiles,
                isLoadingMiles: false,
                error: null,
                requiredMiles,
                isLoadingRequiredMiles,
            }))
        }
    }, [buyData.token?.address, sellData.token?.address, mainWalletAddress, requiredMiles])

    return milesStatus
}
